import { useDispatch, useSelector } from "react-redux"
import { getCompanyInfo } from "reducers/companyInfo"
import { FormProvider, useForm } from "react-hook-form"
import { Modal } from "components/views/modal/Modal"
import React, { useEffect, useState } from "react"
import CarrotClient from "carrot-api/index"
import Settings from "utils/CarrotConfig"
import L10n from "lib/carrot-l10n"
import { CountryCode } from "content/CountryMap"
import { sleep } from "utils/Helpers"
import { reportError } from "utils/ErrorReporting"
import { createErrorBoundary } from "utils/createErrorBoundary"
import { ModalErrorStep } from "components/views/account/phoneNumberUpdate/ModalErrorStep"
import { UpdatePhoneNumberCompletedStep } from "components/views/account/phoneNumberUpdate/UpdatePhoneNumberCompletedStep"
import { fetchUserInfo } from "actions/userActions"
import { HttpErrors } from "utils/HttpErrors"
import { UpdatePhoneNumberFormStep } from "components/views/account/phoneNumberUpdate/UpdatePhoneNumberFormStep"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()
const client = new CarrotClient(Settings.CARROT_BACKEND_URL)

type VerificationFormStep = "FORM" | "COMPLETED" | "FAILED"
export type ResendState = "READY" | "SENDING" | "SENT"
type VerificationCodeState = "NOT_SENT" | "SENT" | "AWAITING_VERIFICATION"

type UpdatePhoneNumberForm = {
  phoneNumber: string
  countryCode: CountryCode
  verificationCode: string
}

const VerificationFormStepComponents: Record<VerificationFormStep, any> = {
  FORM: <UpdatePhoneNumberFormStep />,
  COMPLETED: <UpdatePhoneNumberCompletedStep />,
  FAILED: <ModalErrorStep />
}

export type PhoneNumberUpdateModalContextProps = {
  onSubmit: () => void
  onFinish: () => void
  onResend: () => void
  onPhoneNumberChange: (any: any) => any
  verificationCodeState: VerificationCodeState
  resendState: ResendState
}

export const PhoneNumberUpdateModalContext = React.createContext<PhoneNumberUpdateModalContextProps>(null)

const UpdatePhoneNumberErrorBoundary = createErrorBoundary(ModalErrorStep)

const UpdatePhoneNumberModal = ({ onExit }) => {
  const dispatch = useDispatch()
  const { countryCode } = useSelector(getCompanyInfo)
  const [resendState, setResentState] = useState<ResendState>("SENT")
  const [verificationCodeState, setVerificationCodeState] = useState<VerificationCodeState>("NOT_SENT")
  const [verificationFormStep, setVerificationFormStep] = useState<VerificationFormStep>("FORM")

  const enableResendButton = async () => {
    await sleep(3000)
    setResentState("READY")
  }

  useEffect(() => {
    if (resendState === "SENT") {
      enableResendButton()
    }
  }, [resendState])

  const formMethods = useForm<UpdatePhoneNumberForm>({
    mode: "onChange",
    defaultValues: { countryCode }
  })

  const { reset, setError, getValues } = formMethods

  const onPhoneNumberChange = (event) => {
    if (verificationCodeState === "AWAITING_VERIFICATION") {
      const { target } = event
      const inputName = target.getAttribute("name")
      const inputValue = target.value
      const formData = getValues()
      reset({ ...formData, [inputName]: [inputValue] })
      setVerificationCodeState("NOT_SENT")
    }
  }

  const onResend = async () => {
    const { phoneNumber, countryCode } = getValues()
    setResentState("SENDING")
    await client.startPhoneVerification(phoneNumber, countryCode)
    setResentState("SENT")
    enableResendButton()
  }

  const onFinish = () => {
    dispatch(fetchUserInfo())
    onExit()
  }

  const handlePhoneNumberSubmit = async (formData: UpdatePhoneNumberForm) => {
    const { phoneNumber, countryCode } = formData
    const sentNotificationTimeInMilliseconds = 750

    try {
      await client.startPhoneVerification(phoneNumber, countryCode)
      setVerificationCodeState("SENT")
      await sleep(sentNotificationTimeInMilliseconds)
      setVerificationCodeState("AWAITING_VERIFICATION")
    } catch (error) {
      if (error.name === HttpErrors.NOT_FOUND) {
        setError("phoneNumber", {
          message: l`Please enter a valid phone number`
        })
        return
      }

      reportError(error)
      setVerificationFormStep("FAILED")
    }
  }

  const handleVerificationCodeSubmit = async (formData: UpdatePhoneNumberForm) => {
    const { phoneNumber, countryCode, verificationCode } = formData

    try {
      await client.completePhoneVerification(phoneNumber, verificationCode, countryCode)
      setVerificationFormStep("COMPLETED")
    } catch (error) {
      if (error.name === HttpErrors.NOT_FOUND) {
        setError("verificationCode", {
          message: l`Please enter a valid code`
        })
        return
      }

      reportError(error)
      setVerificationFormStep("FAILED")
    }
  }

  const onSubmit = formMethods.handleSubmit(async (formData) => {
    if (verificationCodeState === "NOT_SENT") {
      await handlePhoneNumberSubmit(formData)
    } else {
      await handleVerificationCodeSubmit(formData)
    }
  })

  const context = {
    onSubmit,
    verificationCodeState,
    onFinish,
    onResend,
    resendState,
    onPhoneNumberChange
  }

  return (
    <Modal underlayClickExits={false} onExit={onExit} titleText={l`Update your phone number`}>
      <div className="tl ph45 mw-600">
        <UpdatePhoneNumberErrorBoundary>
          <PhoneNumberUpdateModalContext.Provider value={context}>
            <FormProvider {...formMethods}>{VerificationFormStepComponents[verificationFormStep]}</FormProvider>
          </PhoneNumberUpdateModalContext.Provider>
        </UpdatePhoneNumberErrorBoundary>
      </div>
    </Modal>
  )
}

export { UpdatePhoneNumberModal }
